// ✅ FIXED: Hook placement issue and rerender trigger logic corrected.

import React, {useRef, useCallback, useEffect, useReducer, useMemo} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import PortfolioRow from '@portfolio/gridcomponents/Portfoliorow/PortfolioRow';
import { InstrumentRowContext } from '@dashboard/contexts/InstrumentRowContext';
import { AnnouncementProvider } from '@dashboard/contexts/ExchangeAnnContext';
import { TickProvider } from '@dashboard/contexts/TickContext';
import MoreDetailsLayout from '@portfolio/gridcomponents/Portfoliorow/MoreDetails/MoreDetailsLayout';
import AnnouncementList from '@dashboard/grid-components/announcements/AnnouncementList';
import ColGroupPortfolio from '@portfolio/PFGridComponents/ColGroupPortfolio';
import {RowSelectionProvider, useRowSelectionContext} from '@portfolio/context/RowSelectionContextProvider';
import {createGlobalKeyHandler} from "@portfolio/utils/createGlobalKeyHandler";
import {normalizeSymbol} from "@dashboard/shared/pfSymbolsRef";

//const ROW_HEIGHT = 50;

const VirtualizedPortfolioTableBody = ({
                                           pauseAutoRefreshForMs,
                                           resumeAutoRefreshNow,
                                           patchFrozenSnapshotInstrumentEntity,
                                           toggleAnnouncementStocksOnly,
                                           announcementStocksOnly,
                                           layouts,
                                           aggregatedSortedStocksCurrentClientTab,
                                           tabID,
                                           setAggregatedSortedStocksCurrentClientTab,
                                           setLayouts,
                                           handleRowDoubleClick,
                                           rowWrapperRef,
                                           initialSymbolToBSEAnnouncementsMap,
                                           initialSymbolToNSEAnnouncementsMap,
                                           shouldUseStockForDisplay,
                                           scrollToSymbol,
                                           setScrollToSymbol,
                                           setSearchQuery,
                                           ROW_HEIGHT
                                       }) => {
    const parentRef = useRef(null);

    const {
        selectedRowKeyRef,
        rowRerenderTriggers,
        visibleRangeRef,
        selectedRowIndexRef,
        updateSelectedRowIndex,
        pageScrollPendingFrames,
        scrollDirectionRef,
        rowVirtualizerRef,
        arrowKeyPendingRef
    } = useRowSelectionContext();

    const [, forceUpdate] = useReducer(x => x + 1, 0);

    const rowVirtualizer = useVirtualizer({
        count: layouts.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: 5,
        getItemKey: (index) => layouts[index].i,
        measureElement: (el) => el.getBoundingClientRect().height,
    });
    rowVirtualizerRef.current = rowVirtualizer; // ✅ set into context

    useEffect(() => {
        const virtualItems = rowVirtualizer.getVirtualItems();
        visibleRangeRef.current = {
            start: virtualItems[0]?.index ?? 0,
            stop: virtualItems.slice(-1)[0]?.index ?? 0,
            pageSize: virtualItems.length
        };
        arrowKeyPendingRef.current = false; // ✅ Unlock next arrow key after scroll
    }, [rowVirtualizer.getVirtualItems()]);

    const virtualItems = rowVirtualizer.getVirtualItems();

    return (
        <div
            ref={parentRef}
            className="scrollable-tbody"
            style={{ height: 400, overflow: 'auto', position: 'relative' }}
            tabIndex={0}
        >
            <div
                style={{ height: rowVirtualizer.getTotalSize(), width: '100%', position: 'relative' }}
            >
                {virtualItems.map((virtualRow) => {
                    const index = virtualRow.index;
                    const layout = layouts[index];
                    const rowKey = layout.i;
                    const stock = aggregatedSortedStocksCurrentClientTab.find(
                        (s) => `${tabID}-${s.clientId}-${s.instrumentEntity.instrumentToken}` === rowKey
                    );

                    if (!stock) return null;

                    return (
                        <div
                            key={rowKey}
                            ref={rowVirtualizer.measureElement}
                            data-row-key={rowKey}
                            data-index={index}   // ✅ Add this line
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualRow.start}px)`
                            }}
                            //onClick={() => updateSelectedRowIndex(rowKey, index)}
                        >
                            <InstrumentRowContext.Provider
                                value={{
                                    uniqueId: rowKey,
                                    stock,
                                    GUIInstrument: stock.instrumentEntity,
                                    aggregatedSortedStocksCurrentClientTab,
                                    setGUIInstruments: setAggregatedSortedStocksCurrentClientTab,
                                    setAggregatedSortedStocksCurrentClientTab,
                                    setLayouts,
                                    tabID,
                                    layouts,
                                    index,
                                    listRef: { current: parentRef.current },
                                    ROW_HEIGHT,
                                    rowVirtualizer,
                                    pauseAutoRefreshForMs,
                                    patchFrozenSnapshotInstrumentEntity,
                                    toggleAnnouncementStocksOnly,
                                    announcementStocksOnly,
                                    resumeAutoRefreshNow
                                }}
                            ><AnnouncementProvider
                                symbol={stock.instrumentEntity.tradingsymbol}
                                initialBSE={initialSymbolToBSEAnnouncementsMap[normalizeSymbol(stock.instrumentEntity.tradingsymbol)] || []}
                                initialNSE={initialSymbolToNSEAnnouncementsMap[normalizeSymbol(stock.instrumentEntity.tradingsymbol)] || []}
                            >

                                <table
                                    className={`pf-table ${
                                        stock.instrumentEntity.forceShowAnnouncements
                                            ? 'pf-table-announcement-highlight'
                                            : ''
                                    }`}
                                    cellPadding="5"
                                    cellSpacing="0"
                                    //onClick={() => updateSelectedRowIndex(rowKey, index)}
                                >
                                    <ColGroupPortfolio/>
                                    <tbody>
                                    <PortfolioRow
                                        shouldUseStockForDisplay={shouldUseStockForDisplay}
                                        onDoubleClick={() => handleRowDoubleClick(stock.symbol)}
                                        rowWrapperRef={rowWrapperRef}
                                        index={index}
                                        rowKey={rowKey}
                                    />
                                    {stock.isDetailsVisible && (
                                        <TickProvider instrumentToken={stock.instrumentEntity.instrumentToken}>
                                            <tr ref={rowWrapperRef} className="tr-detail-item">
                                                <td colSpan="4">
                                                    <MoreDetailsLayout
                                                        setLayouts={setLayouts}
                                                        instrumentToken={stock.instrumentEntity.instrumentToken}
                                                        layoutIndex={index}
                                                        rowWrapperRef={rowWrapperRef}
                                                    />
                                                </td>
                                            </tr>
                                        </TickProvider>
                                    )}
                                    {stock.instrumentEntity.forceShowAnnouncements && (
                                        <tr ref={rowWrapperRef} className="pf-tr-announcement-item">
                                            <td colSpan="8">
                                                <AnnouncementList calledBy="PF"/>
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </AnnouncementProvider>
                            </InstrumentRowContext.Provider>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default VirtualizedPortfolioTableBody;
